import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Box, TextField, Button, Typography, Link } from '@mui/material';
import { authService, emailValidationError, isValidEmail } from '@uniqcamp/shared';
import { useToast } from '../contexts/ToastContext';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const [schoolCode, setSchoolCode] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  /** True when token came from the reset link query string (hide token field). */
  const [tokenFromLink, setTokenFromLink] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = searchParams.get('token') || '';
    setToken(t);
    setTokenFromLink(Boolean(t));
    setEmail(searchParams.get('email') || '');
    setSchoolCode(searchParams.get('schoolCode') || '');
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showToast('error', 'Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      showToast('error', 'Password must be at least 6 characters');
      return;
    }
    if (!token.trim()) {
      showToast('error', 'Reset token is missing. Open the link from your email or paste the token.');
      return;
    }
    const emailErr = emailValidationError(email, { required: true, label: 'Email' });
    if (emailErr) {
      showToast('error', emailErr);
      return;
    }
    if (!schoolCode.trim()) {
      showToast('error', 'School code is required.');
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword(email.trim().toLowerCase(), schoolCode.trim().toUpperCase(), token, newPassword);
      setDone(true);
      showToast('success', 'Password reset successfully. You can now log in.');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      showToast('error', e.response?.data?.message ?? 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>Password reset successfully</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>You can now sign in with your new password.</Typography>
          <Button component={RouterLink} to="/login" variant="contained" fullWidth>Go to login</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>Set new password</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {tokenFromLink
            ? 'Choose a new password. Your reset link is already applied.'
            : 'Enter the token from your email (and your school code and email), then set a new password.'}
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField fullWidth label="School Code" value={schoolCode} onChange={(e) => setSchoolCode(e.target.value)} margin="normal" required />
          <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required />
          {!tokenFromLink && (
            <TextField
              fullWidth
              label="Reset token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              margin="normal"
              required
              helperText="Paste the code from your email if you did not use the link"
            />
          )}
          <TextField
            fullWidth
            label="New password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            required
            inputProps={{ minLength: 6 }}
            helperText="At least 6 characters"
          />
          <TextField
            fullWidth
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !schoolCode.trim() || !isValidEmail(email) || !token.trim() || newPassword.length < 6 || newPassword !== confirmPassword}
            sx={{ mt: 3, py: 1.5 }}
          >
            {loading ? 'Resetting…' : 'Reset password'}
          </Button>
        </form>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" underline="hover">Back to login</Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default ResetPassword;
